import { createSharedComposable } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import type { ComputedRef } from "vue";
import { useSessionContext, useShopwareContext } from "#imports";

export type UsePriceReturn = {
  /**
   * Format price i.e. (2) -> 2.00 $
   */
  getFormattedPrice(value: number | string | undefined, unformatted?: boolean): string;
  /**
   * Update configuration
   */
  update(params: {
    localeCode?: string | undefined;
    currencyCode: string;
  }): void;
  /**
   * Currency code
   */
  currencyCode: ComputedRef<string>;
  /**
   * Currency locale
   */
  currencyLocale: ComputedRef<string>;
};

/**
 * Composable for getting formatted price
 * Set the default currency code and locale in order to format a price correctly
 *
 * @public
 * @category Product
 */
function _usePrice(params?: {
  localeCode?: string | undefined;
  currencyCode: string;
}): UsePriceReturn {
  const { sessionContext } = useSessionContext();
  const { browserLocale } = useShopwareContext();
  
  // Use Shopware language locale or fallback to de-DE for German formatting
  const shopwareLocale = sessionContext.value?.salesChannel?.language?.locale?.code || 'de-DE';
  const currencyLocale = ref<string>(params?.localeCode || shopwareLocale || browserLocale);
  const currencyCode = ref<string>("");

  if (params) {
    currencyCode.value = params.currencyCode;
    if (!params.localeCode) {
      // If no locale provided, use Shopware's locale
      _setLocaleCode(shopwareLocale);
    } else {
      _setLocaleCode(params.localeCode);
    }
  }

  function update(params: {
    localeCode?: string | undefined;
    currencyCode: string;
  }) {
    _setCurrencyCode(params.currencyCode);
    _setLocaleCode(params.localeCode);
  }

  function _setCurrencyCode(code: string) {
    currencyCode.value = code;
  }

  function _setLocaleCode(locale: string | undefined) {
    // Use Shopware locale if no locale provided
    const shopwareLocale = sessionContext.value?.salesChannel?.language?.locale?.code || 'de-DE';
    currencyLocale.value = locale || shopwareLocale;
  }

  /**
   * Format price (2) -> 2.00 $
   */
  function getFormattedPrice(value: number | string | undefined, unformatted?: boolean): string {
    if (typeof value === "undefined") {
      return "";
    }

    if (!currencyLocale.value || !currencyCode.value) {
      return value.toString();
    }
    
    // Format the number without currency
    const formattedNumber = new Intl.NumberFormat(currencyLocale.value, {
      minimumFractionDigits: unformatted ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(+value);
    
    // Get currency symbol
    const currencySymbol = new Intl.NumberFormat(currencyLocale.value, {
      style: "currency",
      currency: currencyCode.value,
    }).formatToParts(0).find(part => part.type === "currency")?.value || currencyCode.value;
    
    return `${formattedNumber} ${currencySymbol}`;
  }

  watch(
    () => sessionContext.value?.currency,
    (newCurrency) => {
      if (newCurrency)
        update({
          // locale code is read only once on SSR because it's unavailable in the context
          currencyCode: newCurrency?.isoCode,
        });
    },
    {
      immediate: true,
    },
  );

  return {
    getFormattedPrice,
    update,
    currencyCode: computed(() => currencyCode.value),
    currencyLocale: computed(() => currencyLocale.value),
  };
}
/**
 * @category Product
 */
export const usePrice = createSharedComposable(_usePrice);
